import { Avatar, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField, Typography, Alert, Collapse, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import useStore, { useStoreActions } from "../../../store";
import athenaService from "../../../services/athenaService";
import verificationService from "../../../services/verificationService";
import Search from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FormHelperText } from '@mui/material';

const formatTime = (timeString) => {
  const [hours, minutes] = timeString.split(':');
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

function AppointmentUserForm({ setScreen }) {

  const [isLoading, setLoading ] = useState(false)
  const [error, setError ] = useState(false)
  const {appointmentDetails, provider, selectedDepartment, isNewPatient, patient} = useStore()
  const t = useStore()
  const { setAppointmentDetails, setPatient, setIsNewPatient } = useStoreActions()
  console.log('🔒🔒🔒',t);
  
  // State for provider image
  const [providerImage, setProviderImage] = useState(null);

  // Patient search and selection states
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState({ 
    firstName: '', 
    lastName: '', 
    dob: null,
    phoneNumber: '' // Changed from email to phoneNumber
  });
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  // Verification flow states
  const [verificationStage, setVerificationStage] = useState('initial'); // 'initial', 'verifying', 'verified'
  const [verificationError, setVerificationError] = useState('');
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [showNewPatientButton, setShowNewPatientButton] = useState(false); // New state for New Patient button
  
  // Simple flag to track if we created a new patient from existing form
  const [cameFromExistingForm, setCameFromExistingForm] = useState(false);
  
  // Store original values of the patient search form
  const [originalPatientSearch, setOriginalPatientSearch] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    sex: "", // No default value for sex - now optional
    privacyPolicy: false,
    textMessages: false,
  });
  
  // Store the properly formatted DOB from enhanced search
  const [formattedDob, setFormattedDob] = useState('');
  
  // State for field-specific errors
  const [fieldErrors, setFieldErrors] = useState({
    sex: "",
    email: "",
  });

  useEffect(() => {
    // Load patients when component mounts
    loadPatients();
  }, []);
  
  // Effect to load provider image
  useEffect(() => {
    // Try to load the provider image if the provider is selected
    if (provider && (provider.id || provider.providerid)) {
      // Convert provider name to lowercase and replace spaces with dashes for filename
      const providerName = `${provider.firstname || ''} ${provider.lastname || ''}`;
      const providerFileName = providerName.toLowerCase().replace(/\s+/g, '-');
      
      try {
        import(`../../../assets/images/providers/${providerFileName}.png`)
          .then(image => {
            setProviderImage(image.default);
          })
          .catch(err => {
            console.error("Could not load provider image:", err);
            setProviderImage(null);
          });
      } catch (error) {
        console.error("Error loading provider image:", error);
        setProviderImage(null);
      }
    }
  }, [provider]);

  // Load patients from localStorage
  const loadPatients = async () => {
    try {
      // Try to get patients from appointments first with department and provider context
      const departmentId = selectedDepartment;
      const providerId = provider?.providerid || provider?.id;
      const appointmentPatients = await athenaService.getPatientsFromAppointments({
        departmentId,
        providerId
      });
      if (appointmentPatients && appointmentPatients.length > 0) {
        console.log(`Using ${appointmentPatients.length} patients from appointments`);
        setPatients(appointmentPatients);
      } else {
        // Fallback to localStorage patients
        console.log('No appointment patients found, using localStorage');
        const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
        setPatients(localPatients);
      }
    } catch (error) {
      console.error("Error loading patients:", error);
      // Fallback to localStorage if API fails
      const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
      setPatients(localPatients);
    }
  };

  // Function to handle patient search
  const handlePatientSearchChange = (e) => {
    const { name, value } = e.target;
    setPatientSearch(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to handle date of birth change
  const handleDobChange = (date) => {
    setPatientSearch(prev => ({
      ...prev,
      dob: date
    }));
  };

  // Start verification process by first checking if patient exists
  const startVerificationProcess = async () => {
    // Require all fields to be filled
    if (!patientSearch.firstName.trim()) {
      setError('Please enter first name');
      return;
    }
    
    if (!patientSearch.lastName.trim()) {
      setError('Please enter last name');
      return;
    }
    
    if (!patientSearch.dob) {
      setError('Please enter date of birth');
      return;
    }
    
    if (!patientSearch.phoneNumber.trim()) {
      setError('Please enter phone number');
      return;
    }
    
    setLoading(true);
    setError(false);
    setShowNewPatientButton(false); // Reset new patient button state
    
    try {
      // First check if patient exists in system with provided details using enhanced best match
      const results = await athenaService.searchPatientsEnhancedBestMatch(patientSearch);
      console.log('Pre-verification enhanced best match search results:', results);
      
      if (!results || results.length === 0) {
        setError('Your details could not be found. Please check your information or create a new patient record.');
        setShowNewPatientButton(true); // Show New Patient button
        setLoading(false);
        return;
      }
      
      // Store results since patient exists
      setPatientSearchResults(results);
      setVerificationSuccess(true);
      setVerificationStage('verified'); // Skip verification step and go directly to verified
    } catch (error) {
      console.error("Error checking patient records:", error);
      setError('Verification failed. Please try again later or contact your provider for assistance.');
      setShowNewPatientButton(true); // Show New Patient button if verification fails
    }
    
    setLoading(false);
  };
  
  // Function to switch to new patient form with prefilled data
  const switchToNewPatientForm = () => {
    // Save the original patient search form data
    setOriginalPatientSearch({...patientSearch});
    
    // Set flag that we came from existing form
    setCameFromExistingForm(true);
    
    // Prefill the new patient form with data from the existing patient form
    setFormData({
      ...formData,
      firstName: patientSearch.firstName,
      lastName: patientSearch.lastName,
      dateOfBirth: patientSearch.dob ? new Date(patientSearch.dob).toISOString().split('T')[0] : '',
      phoneNumber: patientSearch.phoneNumber,
      email: '' // Clear email since we don't have it in the patient search
    });
    
    // Clear the error and new patient button state
    setError(false);
    setShowNewPatientButton(false);
    
    // Switch to new patient form
    setIsNewPatient(true);
    setPatient(null);
    
    console.log('Switched to new patient form');
  };

  // Function to search for patients (only called after successful verification)
  const handleSearchPatients = async () => {
    setLoading(true);
    setError(false);
    
    try {
      // Use the enhanced best match method for better patient matching
      const results = await athenaService.searchPatientsEnhancedBestMatch(patientSearch);
      console.log('Enhanced best match patient search results:', results);
      setPatientSearchResults(results || []);
      
      if (results && results.length === 0) {
        setError('Verification failed. Please double-check your information or contact your provider for assistance.');
      }
    } catch (error) {
      console.error("Error searching patients:", error);
      setError('Verification failed. Please try again later or contact your provider for assistance.');
    } finally {
      setLoading(false);
    }
  };

  // Function to select a patient (now simpler since verification is already done)
  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    
    // Store patient ID (verification is already complete at this point)
    if (patient && patient.patientid) {
      setPatient(parseInt(patient.patientid));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear the error for this specific field when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
    
    // Clear general error when user checks required checkboxes
    if (checked && (name === 'privacyPolicy' || name === 'textMessages')) {
      setError('');
    }
  };

  // Function to handle phone number input with US format
  const handlePhoneInput = (e) => {
    // Get only the digits
    const digits = e.target.value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);
    
    // Format as US phone number
    let formattedPhone = '';
    if (limitedDigits.length > 0) {
      formattedPhone = limitedDigits.replace(
        /^(\d{0,3})(\d{0,3})(\d{0,4})/, 
        (_, p1, p2, p3) => {
          let result = '';
          if (p1) result += `(${p1}`;
          if (p2) result += p1 ? `) ${p2}` : p2;
          if (p3) result += p2 ? `-${p3}` : p3;
          return result;
        }
      );
    }
    
    // Update form state
    setFormData((prev) => ({
      ...prev,
      phoneNumber: formattedPhone
    }));
    
    // Clear phone number field error when user types
    if (fieldErrors.phoneNumber) {
      setFieldErrors(prev => ({
        ...prev,
        phoneNumber: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError(""); // Clear any existing errors
    setLoading(true);
    console.log("Form data submitted:", formData);
    
    // Initialize comprehensive field errors object
    const newFieldErrors = {};
    
    // Validate all required fields at once
    if (!formData.firstName) {
      newFieldErrors.firstName = "First name is required";
    }
    
    if (!formData.lastName) {
      newFieldErrors.lastName = "Last name is required";
    }
    
    if (!formData.phoneNumber) {
      newFieldErrors.phoneNumber = "Phone number is required";
    }
    
    if (!formData.dateOfBirth) {
      newFieldErrors.dateOfBirth = "Date of birth is required";
    } else {
      // Validate date format
      try {
        const [month, day, year] = formData.dateOfBirth.split('/');
        if (!month || !day || !year || isNaN(new Date(year, month-1, day).getTime())) {
          newFieldErrors.dateOfBirth = "Please enter a valid date";
        }
      } catch (err) {
        newFieldErrors.dateOfBirth = "Invalid date format";
      }
    }
    
    // Email validation
    if (!formData.email) {
      newFieldErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newFieldErrors.email = "Please enter a valid email address";
    }
    
    // Checkbox validation
    let checkboxError = "";
    if (!formData.privacyPolicy) {
      checkboxError = "Please agree to the Privacy Policy and Terms of Use";
    }
    
    if (!formData.textMessages) {
      checkboxError = checkboxError 
        ? checkboxError + ", and consent to receive text messages" 
        : "Please consent to receive text messages";
    }
    
    if (checkboxError) {
      setError(checkboxError);
    }
    
    // Check if any validation errors were found
    if (Object.keys(newFieldErrors).length > 0 || checkboxError) {
      setFieldErrors(newFieldErrors);
      setLoading(false);
      return;
    }
    
    // Make sure to preserve all patient details
    setAppointmentDetails({ ...formData });

    try {
      setError('');
      
      // Strip all formatting from phone number (remove brackets, spaces, dashes)
      const cleanPhoneNumber = formData.phoneNumber.replace(/\D/g, '');
      
      // Parse the manually entered date (MM/DD/YYYY) into a proper Date object
      const [month, day, year] = formData.dateOfBirth.split('/').map(num => parseInt(num, 10));
      const dob = new Date(year, month - 1, day);
      
      // First check if patient exists with provided details using enhanced best match
      // Use display value if complete, otherwise use stored value
      console.log('DOB debug before search:', {
        dateOfBirth: formData.dateOfBirth,
        dateOfBirthDisplay: formData.dateOfBirthDisplay,
        formattedDobState: formattedDob
      });
      let dobForSearch = formData.dateOfBirth;
      if (formData.dateOfBirthDisplay && formData.dateOfBirthDisplay.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        dobForSearch = formData.dateOfBirthDisplay;
      }
      console.log('DOB chosen for enhanced search (raw):', new Date(dobForSearch));
      
      const searchResults = await athenaService.searchPatientsEnhancedBestMatch({
        firstName: formData.firstName,
        lastName: formData.lastName,
        dob: new Date(dobForSearch),
        phoneNumber: cleanPhoneNumber
        // email: formData.email
      });
      
      // Store the properly formatted DOB that was actually used in the search (normalized MM/DD/YYYY)
      try {
        const d = new Date(dobForSearch);
        if (!isNaN(d.getTime())) {
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const yyyy = String(d.getFullYear());
          const normalized = `${mm}/${dd}/${yyyy}`;
          setFormattedDob(normalized);
          console.log('Stored formattedDob after search (normalized):', normalized);
        } else {
          // fallback to raw if parsing failed
          setFormattedDob(dobForSearch);
          console.log('Stored formattedDob after search (raw fallback):', dobForSearch);
        }
      } catch (e) {
        setFormattedDob(dobForSearch);
        console.log('Stored formattedDob after search (exception fallback):', dobForSearch);
      }
      
      console.log('Enhanced best match patient search results:', searchResults);
      
      if (searchResults && searchResults.length > 0) {
        // Patient exists, use their ID
        const existingPatient = searchResults[0];
        console.log('Found existing patient:', existingPatient);
        setPatient(parseInt(existingPatient.patientid));
        
        // Extract insurance information directly from enhanced search results
        try {
          // Insurance data is now included in the enhanced search results
          const patientInsurance = existingPatient.insurances || existingPatient.insurance || null;
          
          // Check if patient has insurance by verifying insurances is an array with items
          const hasInsurance = patientInsurance && 
                           Array.isArray(patientInsurance) && 
                           patientInsurance.length > 0;
          
          if (hasInsurance) {
            // Patient has insurance, book the appointment directly
            console.log('Patient has insurance, booking appointment directly');
            
            try {
              // Book the appointment using the athenaService
              const response = await athenaService.bookAppointment({ 
                appointmentid: appointmentDetails.id,
                patientid: parseInt(existingPatient.patientid),
                appointmenttypeid: appointmentDetails.appointmenttypeid,
                notes: '' // No notes when bypassing insurance screen
              });
              
              console.log('Appointment booked successfully:', response);
              // Now proceed to confirmation screen
              setScreen(4);
            } catch (bookingError) {
              console.error('Error booking appointment:', bookingError);
              setError(bookingError.message || "Failed to book appointment. Please try again.");
              // In case of booking error, still allow the user to go to insurance screen
              setScreen(3);
            }
          } else {
            // Patient doesn't have insurance, go to insurance screen
            console.log('Patient needs to provide insurance information');
            setScreen(3); // Go to insurance screen
          }
        } catch (error) {
          console.error('Error checking patient insurance:', error);
          // If there's an error checking insurance, default to the insurance screen
          setScreen(3);
        }
      } else {
        // Patient doesn't exist, create a new one
        console.log('Patient does not exist, creating new patient');
        
        // Make sure to use the clean phone number here too (no formatting)
        // Use the formatted DOB that was successfully used in the enhanced search
        let dobToSend = formattedDob || formData.dateOfBirth;
        // As a safety, normalize here as well to MM/DD/YYYY
        try {
          if (dobToSend) {
            const d = new Date(dobToSend);
            if (!isNaN(d.getTime())) {
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              const yyyy = String(d.getFullYear());
              dobToSend = `${mm}/${dd}/${yyyy}`;
            }
          }
        } catch {}
        
        console.log('DOB debug before create:', {
          dateOfBirth: formData.dateOfBirth,
          dateOfBirthDisplay: formData.dateOfBirthDisplay,
          formattedDobState: formattedDob,
          dobToSend
        });
        const patientData = {
          firstname: formData.firstName,
          lastname: formData.lastName,
          dob: dobToSend,
          mobilephone: cleanPhoneNumber, // Use the clean phone number without formatting
          email: formData.email,
          departmentid: selectedDepartment
        };

        // Only add sex if it was selected and not empty
        if (formData.sex && formData.sex.trim() !== '') {
          patientData.sex = formData.sex === 'male' ? 'M' : 'F';
        }
        
        const user = await athenaService.createPatient(patientData);
        console.log('API Response:', user);
        
        // Check for patientid in different possible formats
        const patientId = user[0]?.patientid || 
                        (user[0]?.patientId) || 
                        (user[0]?.patient?.patientid) || 
                        (user[0]?.patient?.patientId);
        
        console.log('Extracted patient ID:', patientId);
        
        if (patientId) {
          setPatient(parseInt(patientId));
          console.log('Setting patient ID and advancing to screen 3');
          setScreen(3);
        } else {
          console.error('Could not find patient ID in response:', user);
          setError('Created patient but could not find patient ID in response. Please try again.');
        }
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error('Error in handleSubmit:', err);
      
      // Better error debugging - log individual properties
      console.log('Error details:', {
        message: err.message,
        name: err.name,
        code: err.code,
        stack: err.stack,
        response: err.response ? {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        } : 'No response',
        request: err.request ? 'Request present' : 'No request'
      });
      
      let errorMessage = '';
      
      // Handle the exact nested error structure from Athena Health API
      // Based on the screenshot: response has {success, message, error: {error: "message"}} structure
      if (err?.error?.error) {
        // This matches the exact format in the screenshot
        errorMessage = err.error.error;
      } else if (err?.response?.data?.error?.error) {
        // Alternative format if response is nested in axios response
        errorMessage = err.response.data.error.error;
      } else if (err?.message) {
        // Fallback to message
        errorMessage = err.message;
      } else {
        // Last resort generic message
        errorMessage = 'An error occurred while processing your request. Please try again.';
      }
      
      setError(errorMessage);
    }
  };

  useEffect(() => {
    if (appointmentDetails) {
      setFormData(appointmentDetails);
    }
  }, []); 


  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <Avatar
          src={providerImage || "/profile.jpg"}
          alt={provider ? (provider.displayname || `${provider.firstname || ''} ${provider.lastname || ''}`) : "Provider"}
          sx={{
            width: 80,
            height: 80,
            border: 2,
            borderColor: "#383352",
          }}
        />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            { formatDate(appointmentDetails?.date) }, {formatTime(appointmentDetails?.time)}
          </Typography>
          <Typography variant="button">{provider.displayname || 'Dr ' + provider.firstname + provider.lastname}</Typography>
        </Box>
      </Box>

      {/* Always show the new patient form UI */}
      <form onSubmit={handleSubmit} className="pl-0 sm:pl-20" noValidate autoComplete="off">
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body1">First Name *</Typography>
              <TextField 
                fullWidth 
                size="small" 
                name="firstName" 
                value={formData.firstName} 
                onChange={handleChange} 
                variant="outlined" 
                error={!!fieldErrors.firstName}
                helperText={fieldErrors.firstName || ''}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body1">Last Name *</Typography>
              <TextField 
                fullWidth 
                size="small" 
                name="lastName" 
                value={formData.lastName} 
                onChange={handleChange} 
                variant="outlined" 
                error={!!fieldErrors.lastName}
                helperText={fieldErrors.lastName || ''}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body1">Phone Number *</Typography>
              <TextField 
                fullWidth 
                size="small" 
                placeholder="(555) 555-5555" 
                name="phoneNumber" 
                type="tel" 
                value={formData.phoneNumber} 
                onChange={handlePhoneInput} 
                variant="outlined" 
                inputProps={{ 
                  maxLength: 14 // (555) 555-5555 = 14 characters
                }}
                error={!!fieldErrors.phoneNumber}
                helperText={fieldErrors.phoneNumber || "Please enter a 10-digit phone number"}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body1">Email *</Typography>
              <TextField 
                fullWidth 
                size="small" 
                placeholder="email@example.com" 
                name="email" 
                type="email" 
                value={formData.email} 
                onChange={handleChange} 
                variant="outlined"
                required
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
              />
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1">Date of Birth *</Typography>
            <Box sx={{ position: 'relative' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label=""
                  inputFormat="MM/dd/yyyy"
                  value={formData.dateOfBirth ? new Date(formData.dateOfBirth) : null}
                  onChange={(date) => {
                    if (date && !isNaN(date.getTime())) {
                      const month = (date.getMonth() + 1).toString().padStart(2, '0');
                      const day = date.getDate().toString().padStart(2, '0');
                      const year = date.getFullYear();
                      const formattedDate = `${month}/${day}/${year}`;
                      setFormData(prev => ({ ...prev, dateOfBirth: formattedDate }));
                      
                      // Clear dateOfBirth field error when a valid date is selected
                      if (fieldErrors.dateOfBirth) {
                        setFieldErrors(prev => ({
                          ...prev,
                          dateOfBirth: ''
                        }));
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      placeholder="MM/DD/YYYY"
                      sx={{ maxWidth: { xs: "100%", sm: "300px" } }}
                      autoComplete="off"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'off'
                      }}
                      error={!!fieldErrors.dateOfBirth}
                      value={formData.dateOfBirthDisplay || formData.dateOfBirth || ''}
                      onKeyDown={(e) => {
                        // Prevent DatePicker from handling key events during manual entry
                        e.stopPropagation();
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        
                        // Don't immediately update dateOfBirth in formData that gets sent to API
                        // Instead, store the value in a temporary display field
                        setFormData(prev => ({ ...prev, dateOfBirthDisplay: value }));
                        
                        // Clear dateOfBirth field error when user starts typing
                        if (fieldErrors.dateOfBirth) {
                          setFieldErrors(prev => ({
                            ...prev,
                            dateOfBirth: ''
                          }));
                        }
                        
                        // Only validate and update actual dateOfBirth with complete dates
                        if (value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                          try {
                            const [month, day, year] = value.split('/').map(num => parseInt(num, 10));
                            const date = new Date(year, month - 1, day);
                            
                            // Ensure it's a valid date and year is 4 digits
                            if (!isNaN(date.getTime()) && 
                                date.getMonth() === month - 1 && 
                                date.getDate() === day && 
                                date.getFullYear() === year &&
                                year >= 1900 && 
                                year <= new Date().getFullYear() &&
                                year.toString().length === 4) {
                              // Valid date, update the actual dateOfBirth field that will be sent to API
                              setFormData(prev => ({ ...prev, dateOfBirth: value }));
                            } else {
                              // Invalid date, don't update the actual dateOfBirth field
                              console.log('Invalid date format');
                            }
                          } catch (err) {
                            // Invalid date, don't update the actual dateOfBirth field
                            console.log('Invalid date parsing error:', err);
                          }
                        } else {
                          // Incomplete date format, don't update the actual dateOfBirth field
                          // This ensures partial dates like just "2" for the year aren't sent to API
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                          // Sync fully-formed display value into the actual API-bound field
                          setFormData(prev => ({ ...prev, dateOfBirth: value }));
                        }
                      }}
                      helperText={fieldErrors.dateOfBirth || ''}
                    />
                  )}
                />
              </LocalizationProvider>
              {/* Ensure error appears even if DatePicker suppresses it */}
              {fieldErrors.dateOfBirth && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {fieldErrors.dateOfBirth}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Removed "Are you the patient?" question */}

          <FormControl 
            size="small" 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              maxWidth: '500px', 
              mb: 3 
            }}
            error={!!fieldErrors.sex}
            // removed required attribute
          >
            <FormLabel sx={{ width: { xs: 'auto', sm: '200px' }, color: "text.primary", mb: 1 }}>Sex</FormLabel>
            <RadioGroup row name="sex" value={formData.sex} onChange={handleChange} sx={{ gap: 2 }}>
              <FormControlLabel value="male" control={<Radio size="small" />} label="Male" />
              <FormControlLabel value="female" control={<Radio size="small" />} label="Female" />
            </RadioGroup>
            {fieldErrors.sex && <FormHelperText error>{fieldErrors.sex}</FormHelperText>}
          </FormControl>

          <Box sx={{ mb: 4 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  name="privacyPolicy" 
                  checked={formData.privacyPolicy} 
                  onChange={handleCheckboxChange}
                  inputProps={{ 
                    'aria-label': 'checkbox',
                    'aria-required': 'true'
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  * I have read and agreed to the <a href="#" underline="always">Privacy Policy</a> and <a href="#" underline="always">Terms of Use</a> that I am at least 13 and have the authority to make this appointment.
                </Typography>
              }
            />
            
            {/* Add margin/spacing between checkboxes */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
              control={
                <Checkbox 
                  name="textMessages" 
                  checked={formData.textMessages} 
                  onChange={handleCheckboxChange}
                  inputProps={{ 
                    'aria-label': 'checkbox',
                    'aria-required': 'true'
                  }}
                />
              }
              label={<Typography variant="body2">I agree to receive text messages from this practice and understand that message frequency and data rates may apply.</Typography>}
            />
            </Box>
          </Box>

         {error && <Box my={2} p={2} bgcolor="#ffebee" borderRadius={1}>
                <Typography color="error" fontWeight={500}>
                  {error.includes("Phone") ? "Invalid phone number . Please enter a valid phone number" : 
                   error.includes("DOB") || error.includes("date of birth") ? "Invalid date of birth . Please enter a valid date of birth" : 
                   error.includes("email") ? "Invalid email format . Please enter a valid email" : 
                   error.includes("Required") ? "Please fill all required fields" : 
                   error.includes("Patient already exists") ? "Patient already exists in the system" :
                   error}
                </Typography>
          </Box>}

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              onClick={() => {
                // Same Back button behavior as the other one
                if (isNewPatient && cameFromExistingForm) {
                  // If we're in new patient form and came from existing patient form,
                  // go back to existing patient form instead of appointment slots
                  console.log('New Patient Form: Going back to existing patient form');
                  
                  setIsNewPatient(false);
                  setVerificationStage('initial');
                  setError(false);
                  setShowNewPatientButton(false);
                  
                  // Restore original patient search if available
                  if (originalPatientSearch) {
                    setPatientSearch(originalPatientSearch);
                  }
                  
                  setCameFromExistingForm(false);
                } else {
                  // Otherwise, go back to appointment slots
                  console.log('New Patient Form: Going back to appointment slots');
                  setScreen(1);
                }
              }}
              variant="outlined"
              sx={{
                px: 4,
                color: "#383352",
                borderColor: "#383352",
              }}
              disabled={isLoading}
              >
              BACK
            </Button>
            <Button
            disabled={isLoading}
            type="submit"
              variant="contained"
              sx={{
                px: 4,
                bgcolor: "#383352",
                color: "white",
              }}
            >
              { isLoading ? <><CircularProgress size={20} /> LOADING</> : 'CONTINUE'}
            </Button>
          </Box>
        </form>
      </div>
    );
}

export default AppointmentUserForm;
